import * as importTypesActions from './import-types.actions';
import { Action } from '../../shared/interfaces/shared';
import { AppState } from '../../shared/interfaces/state/app-state';
import * as importTypesWebApi from '../webapis/import-types.webapi';
import * as sharedServices from '../../shared/services/shared.service';
import * as importTypeServices from '../services/import-types.service';
import { Store } from 'redux';
import { ActionsObservable } from 'redux-observable';
import { of } from 'rxjs';
import { catchError, concatMap, map, switchMap } from 'rxjs/operators';
import { ImportLinesRequest } from '../interfaces/import-lines';
import { NewImportType } from '../interfaces/dto/new-import-type';
import { UploadFile } from '../interfaces/upload-file';

export const getImportTypes$ = (action$: ActionsObservable<Action<string>>, _store: Store<AppState>) =>
    action$.ofType(importTypesActions.GET_IMPORT_TYPES_REQ).pipe(
        map(action => action.payload),
        concatMap(() => importTypesWebApi.getImportTypes().pipe(
            map(importTypes => importTypesActions.getImportTypesOk(importTypes)),
            catchError(err => of(importTypesActions.getImportTypesFail(err))),
        )),
    );

export const createImportType$ = (action$: ActionsObservable<Action<NewImportType>>, _store: Store<AppState>) =>
    action$.ofType(importTypesActions.CREATE_IMPORT_TYPE_REQ).pipe(
        map(action => action.payload!),
        concatMap(newImportType => {
                sharedServices.setLoading(true);
                return importTypesWebApi.createImportType(newImportType).pipe(
                    map(importType => {
                        sharedServices.setLoading(false);
                        importTypeServices.setAddImportTypeModal(false);
                        sharedServices.setNotification({ visible: true, message: `Le type d'import ${importType.name} ${importType.lineType} a été créé.` });
                        return importTypesActions.createImportTypeOk(importType);
                    }),
                    catchError(err => {
                        sharedServices.setLoading(false);
                        // Le formulaire reste ouvert : la saisie n'est pas perdue et l'erreur y est affichée
                        sharedServices.setNotification({ visible: true, message: `Impossible de créer le type d'import.` });
                        return of(importTypesActions.createImportTypeFail(err));
                    }),
            );
        }),
    );

export const refreshTotals$ = (action$: ActionsObservable<Action<null>>, _store: Store<AppState>) =>
    action$.ofType(importTypesActions.REFRESH_TOTALS_REQ).pipe(
        concatMap(() => {
                sharedServices.setLoading(true);
                return importTypesWebApi.refreshTotals().pipe(
                    map(importTypes => {
                        sharedServices.setLoading(false);
                        // La réponse est la liste complète recalculée : on réutilise l'action de chargement
                        return importTypesActions.getImportTypesOk(importTypes);
                    }),
                    catchError(err => {
                        sharedServices.setLoading(false);
                        // La page Imports est accessible aux non-admins : sans ce message, le 401
                        // renvoyé par l'endpoint passerait totalement inaperçu
                        sharedServices.setNotification({ visible: true, message: `Impossible de recalculer les totaux.` });
                        return of(importTypesActions.refreshTotalsFail(err));
                    }),
            );
        }),
    );

export const clearImportType$ = (action$: ActionsObservable<Action<number>>, _store: Store<AppState>) =>
    action$.ofType(importTypesActions.CLEAR_IMPORT_TYPE_REQ).pipe(
        map(action => action.payload!),
        concatMap(id => {
                sharedServices.setLoading(true);
                return importTypesWebApi.clearImportType(id).pipe(
                    map(importType => {
                        sharedServices.setLoading(false);
                        sharedServices.setNotification({ visible: true, message: `Les lignes ${importType.lineType} de l'${importType.name} ont été supprimées.` });
                        return importTypesActions.clearImportTypeOk(importType);
                    }),
                    catchError(err => {
                        sharedServices.setLoading(false);
                        sharedServices.setNotification({ visible: true, message: `Failed to clear lignes.`});
                        return of(importTypesActions.clearImportTypeFail(err));
                    }),
            );
        }),
    );

export const uploadGeoJsonFile$ = (action$: ActionsObservable<Action<UploadFile>>, _store: Store<AppState>) =>
    action$.ofType(importTypesActions.UPLOAD_GEOJSON_FILE_REQ).pipe(
        map(action => action.payload!),
        concatMap(uploadFile => {
                importTypeServices.setFileImporting(true);
                return importTypesWebApi.uploadGeoJsonFile(uploadFile).pipe(
                    map(importType => {
                        importTypeServices.setFileImporting(false);
                        sharedServices.setNotification({ visible: true, message: `Import lancé pour ${importType.name} ${importType.lineType}.` });
                        return importTypesActions.uploadGeoJsonFileOk(importType);
                    }),
                    catchError(err => {
                        importTypeServices.setFileImporting(false);
                        // sharedServices.setNotification({ visible: true, message: `Failed to upload lignes.`});
                        return of(importTypesActions.uploadGeoJsonFileFail(err));
                    }),
            );
        }),
    );

// switchMap et non concatMap : chaque déplacement de la carte relance la requête. Enchaîner les
// réponses ferait clignoter la carte avec des emprises périmées, seule la dernière doit être dessinée.
export const getImportLines$ = (action$: ActionsObservable<Action<ImportLinesRequest>>, _store: Store<AppState>) =>
    action$.ofType(importTypesActions.GET_IMPORT_LINES_REQ).pipe(
        map(action => action.payload!),
        switchMap(request => importTypesWebApi.getImportLines(request).pipe(
            map(importLines => importTypesActions.getImportLinesOk(importLines)),
            catchError(err => {
                sharedServices.setNotification({ visible: true, message: `Impossible d'afficher les lignes sur la carte.` });
                return of(importTypesActions.getImportLinesFail(err));
            }),
        )),
    );
